import { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { getNetworkPlans, getNetworkPlanById } from "../../app/api";
import FloatingShapes from "../common/FloatingShapes";

const WEEKDAYS_MN = ['Ням', 'Даваа', 'Мягмар', 'Лхагва', 'Пүрэв', 'Баасан', 'Бямба'];
const WEEKDAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Огноог местийн цагаар түлхүүр болгоно — toISOString нь UTC руу шилжүүлж өдрийг гүйлгэдэг. */
const dayKey = (value: any) => {
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dayLabel = (key: string, isEn: boolean) => {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const names = isEn ? WEEKDAYS_EN : WEEKDAYS_MN;
  return { date: `${m}/${d}`, weekday: names[dt.getDay()] };
};

export const NetworkPlanView = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [plan, setPlan] = useState<any>(null);

  // Батлагдсан төлөвлөгөөнүүдийн жагсаалт
  useEffect(() => {
    let cancelled = false;
    getNetworkPlans(new URLSearchParams({ limit: '20', offset: '0' })).then((res: any) => {
      if (cancelled) return;
      const list = res?.data?.results ?? (Array.isArray(res?.data) ? res.data : []);
      setPlans(list);
      if (list.length > 0) setSelectedId(list[0].id ?? list[0]._id);
      else setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setPlans([]);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Сонгосон төлөвлөгөөний дэлгэрэнгүй
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setLoading(true);
    getNetworkPlanById(selectedId).then((res: any) => {
      if (cancelled) return;
      setPlan(res?.data ?? null);
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setPlan(null);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedId]);

  const slots: any[] = useMemo(() => (plan?.slots ?? []), [plan]);

  const dates = useMemo(() => {
    const set = new Set<string>();
    slots.forEach((s) => { const k = dayKey(s.date); if (k) set.add(k); });
    return Array.from(set).sort();
  }, [slots]);

  const timeSlots = useMemo(() => {
    const set = new Set<string>();
    slots.forEach((s) => { if (s.timeSlot) set.add(s.timeSlot); });
    return Array.from(set).sort();
  }, [slots]);

  const teams = useMemo(() => {
    const set = new Set<string>();
    slots.forEach((s) => set.add(s.team || ''));
    const arr = Array.from(set);
    return arr.length > 0 ? arr.sort() : [''];
  }, [slots]);

  const cellIndex = useMemo(() => {
    const map: Record<string, any> = {};
    slots.forEach((s) => {
      map[`${dayKey(s.date)}|${s.timeSlot}|${s.team || ''}`] = s;
    });
    return map;
  }, [slots]);

  return (
    <>
      {loading && (
        <div className="w-full h-full fixed top-0 left-0 bg-white/95 z-50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-800 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Dark hero header */}
      <div className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-secondary-500/15 blur-[100px]" />
        </div>
        <FloatingShapes variant="default" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative pt-40 pb-16 sm:pt-48 sm:pb-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-secondary-300 backdrop-blur-sm">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary-400 animate-pulse" />
              {t('networkPlan.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight">
              <span className="bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                {t('networkPlan.title')}
              </span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              {t('networkPlan.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-neutral-50 py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

          {plans.length === 0 && !loading && (
            <div className="py-20 text-center text-neutral-500">{t('networkPlan.noPlans')}</div>
          )}

          {plans.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {plans.map((p: any) => {
                const id = p.id ?? p._id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSelectedId(id)}
                    className={`px-5 py-2 rounded-full text-sm font-bold transition-all duration-200 ${selectedId === id
                      ? 'bg-primary-600 text-white shadow-lg'
                      : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'}`}
                  >
                    {p.title}
                  </button>
                );
              })}
            </div>
          )}

          {plan && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-900">{plan.title}</h2>
                {plan.eelj_id?.title && (
                  <p className="mt-1 text-neutral-500">{plan.eelj_id.title}</p>
                )}
              </div>

              {/* Хуваарийн хүснэгт */}
              {dates.length > 0 ? (
                <div className="overflow-x-auto bg-white rounded-2xl shadow-lg border border-neutral-200">
                  <table className="min-w-[900px] w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-neutral-50">
                        <th className="text-left font-bold text-neutral-700 px-4 py-3 border-b border-neutral-200 whitespace-nowrap">
                          {t('networkPlan.time')}
                        </th>
                        {teams.length > 1 && (
                          <th className="text-left font-bold text-neutral-700 px-4 py-3 border-b border-neutral-200 whitespace-nowrap">
                            {t('networkPlan.team')}
                          </th>
                        )}
                        {dates.map((d) => {
                          const l = dayLabel(d, isEn);
                          return (
                            <th key={d} className="text-left font-bold text-neutral-700 px-4 py-3 border-b border-neutral-200 whitespace-nowrap">
                              <div>{l.date}</div>
                              <div className="text-xs font-medium text-neutral-400">{l.weekday}</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {timeSlots.map((time) => (
                        teams.map((team, ti) => (
                          <tr key={`${time}|${team}`} className="align-top">
                            {ti === 0 && (
                              <td rowSpan={teams.length}
                                className="px-4 py-3 border-b border-neutral-100 font-bold text-neutral-900 whitespace-nowrap">
                                {time}
                              </td>
                            )}
                            {teams.length > 1 && (
                              <td className="px-4 py-3 border-b border-neutral-100 text-neutral-500 whitespace-nowrap">
                                {team || '—'}
                              </td>
                            )}
                            {dates.map((d) => {
                              const s = cellIndex[`${d}|${time}|${team}`];
                              return (
                                <td key={d} className="px-4 py-3 border-b border-neutral-100 min-w-[160px]">
                                  {s ? (
                                    <>
                                      <div className="font-semibold text-neutral-900">
                                        {s.program_id?.title || s.customTitle || '—'}
                                      </div>
                                      {s.location && <div className="text-xs text-neutral-500 mt-0.5">{s.location}</div>}
                                      {Array.isArray(s.responsible) && s.responsible.length > 0 && (
                                        <div className="text-xs text-neutral-400 mt-0.5">
                                          {s.responsible.map((r: any) => `${r.lastName ?? ''} ${r.firstName ?? ''}`.trim()).filter(Boolean).join(', ')}
                                        </div>
                                      )}
                                    </>
                                  ) : null}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !loading && <div className="py-12 text-center text-neutral-500">{t('networkPlan.noSlots')}</div>
              )}

              {/* Өдрийн дэглэм */}
              {Array.isArray(plan.dayRegime) && plan.dayRegime.length > 0 && (
                <section className="mt-12">
                  <h3 className="text-xl font-extrabold text-neutral-900 mb-4">{t('networkPlan.dayRegime')}</h3>
                  <div className="bg-white rounded-2xl shadow-lg border border-neutral-200 divide-y divide-neutral-100">
                    {plan.dayRegime.map((r: any, i: number) => (
                      <div key={i} className="flex gap-6 px-5 py-3">
                        <span className="font-bold text-neutral-900 whitespace-nowrap w-32">{r.time}</span>
                        <span className="text-neutral-600">{r.activity}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Зөвлөмж */}
              {Array.isArray(plan.recommendations) && plan.recommendations.length > 0 && (
                <section className="mt-12">
                  <h3 className="text-xl font-extrabold text-neutral-900 mb-4">{t('networkPlan.recommendations')}</h3>
                  <ol className="list-decimal pl-6 space-y-2 text-neutral-700 leading-relaxed">
                    {plan.recommendations.map((r: string, i: number) => <li key={i}>{r}</li>)}
                  </ol>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default NetworkPlanView;
